import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import dayjs from 'helpers/dayjs';
import PropTypes from 'prop-types';

const PRESETS = [
  { label: 'Last Month',    fn: () => ({ s: dayjs().subtract(1, 'month').startOf('month'),    e: dayjs().subtract(1, 'month').endOf('month')    }) },
  { label: 'This Month',    fn: () => ({ s: dayjs().startOf('month'),                         e: dayjs().endOf('month')                         }) },
  { label: 'Last Quarter',  fn: () => ({ s: dayjs().subtract(1, 'quarter').startOf('quarter'), e: dayjs().subtract(1, 'quarter').endOf('quarter') }) },
  { label: 'Next Quarter',  fn: () => ({ s: dayjs().add(1, 'quarter').startOf('quarter'),      e: dayjs().add(1, 'quarter').endOf('quarter')      }) },
  { label: 'This Year',     fn: () => ({ s: dayjs().startOf('year'),                          e: dayjs().endOf('year')                          }) },
  { label: 'Last Year',     fn: () => ({ s: dayjs().subtract(1, 'year').startOf('year'),       e: dayjs().subtract(1, 'year').endOf('year')       }) },
];

/**
 * DateRangePicker — compact pill trigger + calendar dropdown.
 * Renders the dropdown via a React portal to avoid overflow clipping.
 *
 * Props:
 *   label      — placeholder label on the trigger button
 *   startDate  — selected start date string (YYYY-MM-DD)
 *   endDate    — selected end date string (YYYY-MM-DD)
 *   onChange   — ({ startDate, endDate }) callback
 *   className  — optional extra class on the wrapper div
 */
const DateRangePicker = ({ label, startDate, endDate, onChange, className }) => {
  const [open,  setOpen]  = useState(false);
  const [range, setRange] = useState([
    startDate ? new Date(startDate) : null,
    endDate   ? new Date(endDate)   : null,
  ]);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });

  const triggerRef = useRef(null);

  // Sync range state when controlled props change
  useEffect(() => {
    setRange([
      startDate ? new Date(startDate) : null,
      endDate   ? new Date(endDate)   : null,
    ]);
  }, [startDate, endDate]);

  // Calculate dropdown position — flips up if not enough space below
  const handleToggle = useCallback(() => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const DROPDOWN_H = 420;
      const DROPDOWN_W = 316;
      const top = window.innerHeight - rect.bottom >= DROPDOWN_H
        ? rect.bottom + 6
        : Math.max(rect.top - DROPDOWN_H - 6, 6);
      const left = Math.min(rect.left, window.innerWidth - DROPDOWN_W - 8);
      setDropPos({ top, left });
    }
    setOpen(o => !o);
  }, [open]);

  const hasValue = !!(startDate || endDate);
  const displayLabel = hasValue
    ? `${startDate ? dayjs(startDate).format('DD MMM YY') : '…'} → ${endDate ? dayjs(endDate).format('DD MMM YY') : '…'}`
    : label;

  const applyRange = ([s, e]) => {
    setRange([s, e]);
    if (s && e) {
      onChange({
        startDate: dayjs(s).format('YYYY-MM-DD'),
        endDate:   dayjs(e).format('YYYY-MM-DD'),
      });
      setOpen(false);
    }
  };

  const applyPreset = ({ s, e }) => {
    const start = s.toDate();
    const end   = e.toDate();
    setRange([start, end]);
    onChange({ startDate: s.format('YYYY-MM-DD'), endDate: e.format('YYYY-MM-DD') });
    setOpen(false);
  };

  const clear = (ev) => {
    ev.stopPropagation();
    setRange([null, null]);
    onChange({ startDate: '', endDate: '' });
  };

  const dropdown = open && ReactDOM.createPortal(
    <>
      {/* Invisible backdrop to close on outside click */}
      <div className="tw-fixed tw-inset-0 tw-z-[9998]" onClick={() => setOpen(false)} />
      <div
        className="tw-fixed tw-z-[9999] tw-bg-white tw-rounded-lg tw-shadow-2xl tw-border tw-border-slate-200 tw-p-3"
        style={{ top: dropPos.top, left: dropPos.left, width: 310 }}
      >
        {/* Quick presets — 3-per-row compact grid */}
        <div className="tw-mb-2">
          <p className="tw-text-[10px] tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-1.5">
            Quick Select
          </p>
          <div className="tw-grid tw-grid-cols-3 tw-gap-1">
            {PRESETS.map(p => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p.fn())}
                className="tw-text-[11px] tw-px-2 tw-py-1 tw-rounded-md tw-border tw-border-slate-200 tw-bg-slate-50 tw-text-slate-600 hover:tw-bg-blue-50 hover:tw-border-blue-300 hover:tw-text-blue-700 tw-cursor-pointer tw-transition-colors tw-font-medium tw-text-center tw-truncate"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="tw-border-t tw-border-slate-100 tw-my-2" />
        <p className="tw-text-[10px] tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-1.5">
          Custom Range
        </p>

        <DatePicker
          selected={range[0]}
          onChange={applyRange}
          startDate={range[0]}
          endDate={range[1]}
          selectsRange
          inline
          monthsShown={1}
          dateFormat="dd-MMM-yyyy"
          calendarClassName="!tw-border-0 !tw-shadow-none"
        />
      </div>
    </>,
    document.body
  );

  return (
    <div ref={triggerRef} className={`tw-relative tw-inline-block ${className || ''}`}>
      <button
        type="button"
        onClick={handleToggle}
        className={`tw-inline-flex tw-items-center tw-gap-2 tw-h-8 tw-px-3 tw-rounded tw-border tw-text-xs tw-font-medium tw-cursor-pointer tw-transition-colors tw-whitespace-nowrap ${
          hasValue
            ? 'tw-bg-blue-50 tw-border-blue-400 tw-text-blue-700'
            : 'tw-bg-white tw-border-slate-300 tw-text-slate-600 hover:tw-border-blue-400 hover:tw-text-blue-600'
        }`}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
          <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4z" />
        </svg>
        {displayLabel}
        {hasValue && (
          <span
            onClick={clear}
            className="tw-ml-1 tw-rounded-xl tw-w-3.5 tw-h-3.5 tw-flex tw-items-center tw-justify-center tw-bg-blue-200 hover:tw-bg-blue-300 tw-text-blue-700 tw-cursor-pointer"
          >
            ×
          </span>
        )}
      </button>

      {dropdown}
    </div>
  );
};

DateRangePicker.propTypes = {
  label:     PropTypes.string,
  startDate: PropTypes.string,
  endDate:   PropTypes.string,
  onChange:  PropTypes.func.isRequired,
  className: PropTypes.string,
};

DateRangePicker.defaultProps = {
  label:     'Select date range',
  startDate: '',
  endDate:   '',
  className: '',
};

export default DateRangePicker;
